import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { memo, useCallback, useEffect, useState } from 'react'
import { Alert, Container, Modal, Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import AppSearch from '../../components/common/AppSearch';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { accessLevelOptions, datasourceOptions } from '../../utils';
import { Checkbox, Select } from "antd";
import { useMemo } from "react";
import { debounce } from "lodash";
import { Datasources } from "../../api/Datasources";
import DatasourceItem from "../../components/list-items/DatasourceItem";
import { QueryDetails, VizClient } from '../../api/VizClient';
import { Datasource } from '../../models/Datasource';
import { AccessLevel } from '../../models/generics';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';
import AppLoader from '../../components/common/AppLoader';

function DatasourcesList() {
  const [datasources, setDatasources] = useState<Datasource[] | null>();
  // const [totalDatasets, setTotalDatasets] = useState();
  const [error, setError] = useState(false);
  const [datasourceTypes, setDatasourceTypes] = useState<string[]>([]);

  const [searchText, setSearchText] = useState('');
  const [accessLevelFilter, setAccessLevelFilter] = useState<AccessLevel>();
  const [typeFilter, setTypeFilter] = useState<string>();
  const [defaultFilter, setDefaultFilter] = useState(false);
  // const [myDatasets, setMyDatasets] = useState(false);
  // const [itemsPerPage, setItemsPerPage] = useState(20);
  // const [currentPage, setCurrentPage] = useState(1);

  // const [sortDesc, setSortDesc] = useState(true);

  const [datasource, setDatasource] = useState<Datasource | null>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const navigate = useNavigate();

  const assetLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasources"));

  // useEffect(() => {
  //   reset();
  //   // eslint-disable-next-line
  // }, [currentPage]);

  // handling current page higher than actual available pages edge case
  // useEffect(() => {
  //   if (currentPage > 1) setCurrentPage(1);
  //   else reset();
  //   // eslint-disable-next-line
  // }, [itemsPerPage, sortDesc, searchText, statusFilter, accessLevelFilter, myDatasets]);

  const getDatasources = useCallback(() => {
    Datasources.getDatasources()
      .then(
        ({ data }) => setDatasources(data),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasources-error', message, `${assetLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
          setError(true);
        }
      );
  }, [assetLabels, showToast]);

  useEffect(() => {
    getDatasources()
    // eslint-disable-next-line
  }, []);

  const initDatasourceTypes = useCallback(() => {
    Datasources.getDatasourceTypes()
      .then(
        ({ data }) => setDatasourceTypes(data),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('datasource-types-error', message, `Datasource Types Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [showToast]);

  useEffect(() => {
    initDatasourceTypes();
    // eslint-disable-next-line
  }, []);

  const handleDeleteClose = useCallback(() => {
    setShowDeleteModal(false);
    setDatasource(null);
  }, []);

  const navigateToCreation = () => navigate('/datasources/create');
  // const changeSortMode = () => setSortDesc(!sortDesc);

  const reset = useCallback(() => {
    setError(false);
    setDatasources(null);
    getDatasources();
  }, [getDatasources]);

  const editDatasource = useCallback((datasource: Datasource) => (evt: React.MouseEvent) => {
    evt.stopPropagation();
    navigate(`/datasources/${datasource.id}/edit`);
  }, [navigate]);

  const testDatasource = useCallback((datasource: Datasource) => (evt: React.MouseEvent) => {
    evt.stopPropagation();
    showLoader();
    const body = new QueryDetails(datasource);

    const promise = datasource.type === 'messageBroker'
      ? VizClient.getTopics(body)
      : VizClient.getDatabases(body);

    promise.then(
      () => {
        hideLoader();
        showToast('connection-success', `Successfully connected to ${assetLabels.singular}`, "Connection Valid", 'success');
      },
      err => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('connection-error', message, `Connection Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }, [showLoader, hideLoader, showToast, assetLabels]);

  const onDeleteDatasource = useCallback((datasource: Datasource) => (evt: React.MouseEvent) => {
    evt.stopPropagation();
    setShowDeleteModal(true);
    setDatasource(datasource);
  }, []);

  const deleteDatasource = useCallback(() => {
    showLoader();

    Datasources.deleteDatasource(datasource?.id as number)
      .then(
        () => {
          hideLoader();
          reset();
          handleDeleteClose();
          showToast('datasource-success', `${assetLabels.singular} deleted successfully`, `${assetLabels.singular} Deleted`, 'success');
        },
        () => {
          hideLoader();
          showToast('datasource-error', `Could not delete ${assetLabels.singular}`, `${assetLabels.singular} Error`, 'danger');
        }
      )
  }, [datasource, showLoader, hideLoader, showToast, reset, handleDeleteClose, assetLabels]);

  const handleFilterChange = useCallback((filter: 'search' | 'accessLevel' | 'type' | 'default') => (evt: any) => {
    switch (filter) {
      case 'search':
      default:
        setSearchText(evt);
        break;

      case 'accessLevel':
        setAccessLevelFilter(evt);
        break;

      case 'type':
        setTypeFilter(evt);
        break;

      case 'default':
        setDefaultFilter(evt.target.checked);
        break;
    }
  }, []);

  const debounceSearchChange = useMemo(() => debounce(handleFilterChange("search"), 500), [handleFilterChange]);

  // const handleItemsPerPageChange = (page, size) => {
  //   changePage(page);
  //   setItemsPerPage(size);
  // }
  // const changePage = (page) => setCurrentPage(page);
  // const handleMyDatasetsChange = (evt) => setMyDatasets(evt.target.checked);
  const datasourceDetail = useCallback((datasource: Datasource) => () => navigate(`/datasources/${datasource.id}`), [navigate]);

  // cleanup debounced handler
  useEffect(() => {
    return () => {
      debounceSearchChange.cancel();
    }
  }, [debounceSearchChange]);

  const filteredDatasources = useMemo(() => {
    return datasources
      ?.filter(datasource => !typeFilter || datasource.type === typeFilter)
      .filter(datasource => !accessLevelFilter || datasource.accessLevel === accessLevelFilter)
      .filter(datasource => !defaultFilter || datasource.default)
      .filter(datasource => datasource.name.toLowerCase().includes(searchText.toLowerCase())) || null;
  }, [datasources, searchText, typeFilter, accessLevelFilter, defaultFilter]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert variant="danger" className="py-2">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred, please try again later.</span>
      </Alert>
    );

    if (!datasources) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return filteredDatasources?.length === 0
      ? (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={solid('info-circle')} />
          <span className="ms-2">No {assetLabels.plural} found.</span>
        </Alert>
      )
      : (
        <Row>
          {
            filteredDatasources?.map(datasource => (
              <Col xs={12} sm={6} md={4} lg={3} className="p-2" key={datasource.id}>
                <DatasourceItem
                  datasource={datasource}
                  onClick={datasourceDetail(datasource)}
                  onDelete={onDeleteDatasource(datasource)}
                  onEdit={editDatasource(datasource)}
                  onTest={testDatasource(datasource)}
                />
              </Col>
            ))
          }
        </Row>
      )
  }, [error, datasources, assetLabels, filteredDatasources, datasourceDetail, onDeleteDatasource, editDatasource, testDatasource]);

  // TODO check if we can use AppBrowsingToolbar
  const renderToolbar = () => {
    return (
      <>
        <Row className="mb-2 align-items-center">
          <Col>
            <h4 className="mb-0">{assetLabels.plural} List</h4>
          </Col>

          <Col xs={6} md={4} className="text-end">
            <Button size="sm" variant="success" onClick={navigateToCreation}>
              <FontAwesomeIcon icon={solid('plus')} />
              <span className="d-none d-sm-inline ms-2">Register {assetLabels.singular}</span>
            </Button>
          </Col>
        </Row>

        <Row className="align-items-center">
          <Col md={3} className="mb-2">
            <AppSearch placeholder={`Search ${assetLabels.singular}...`} onChange={debounceSearchChange} />
          </Col>
          <Col md={2} className="mb-2">
            <Select
              allowClear
              placeholder="Select privacy level"
              value={accessLevelFilter}
              onChange={handleFilterChange('accessLevel')}
              className="w-100"
            >
              {accessLevelOptions
                .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true)
                .map((option, index) => (
                  <Select.Option key={index} value={option.value} label={option.label}>
                    <div>
                      <FontAwesomeIcon icon={option.icon} fixedWidth />
                      <span className="ms-2">{option.label}</span>
                    </div>
                  </Select.Option>
                ))}
            </Select>
          </Col>
          <Col md={2} className="mb-2">
            <Select
              allowClear
              placeholder={`Select ${assetLabels.singular} type`}
              value={typeFilter}
              onChange={handleFilterChange('type')}
              loading={!datasourceTypes}
              className="w-100"
            >
              {datasourceTypes
                .map(type => datasourceOptions.find(opt => opt.key === type) || ({ label: type, value: type, icon: undefined }))
                .map((option, index) => (
                  <Select.Option key={index} value={option.value} label={option.label}>
                    <div>
                      {option.icon && <FontAwesomeIcon icon={option.icon} fixedWidth />}
                      <span className="ms-2">{option.label}</span>
                    </div>
                  </Select.Option>
                ))}
            </Select>
          </Col>
          <Col className="mb-2">
            <Checkbox checked={defaultFilter} onChange={handleFilterChange('default')}>Default</Checkbox>
          </Col>
          {filteredDatasources && (
            <Col md={2} className="text-end">
              {filteredDatasources.length > 0 && <h6 className="text-muted mb-0">({filteredDatasources.length} found)</h6>}
            </Col>
          )}
        </Row>
        {/* <Row>
          <Col md={2}>
            <Checkbox checked={myDatasets} onChange={handleMyDatasetsChange}>My Datasets</Checkbox>
            <Checkbox className="ms-3" checked={defaultOnly} onChange={handleDefaultOnlyChange}>Default Only</Checkbox>
            <Checkbox className="ms-3" checked={activeSchedules} onChange={handleSchedulesChange}>Active Schedules</Checkbox>
          </Col>
          <Col className="text-center">
            <Pagination
              disabled={!datasources}
              total={totalDatasets}
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
              pageSize={itemsPerPage}
              current={currentPage}
              onChange={changePage}
              onShowSizeChange={handleItemsPerPageChange}
            />
          </Col>
          <Col md={2}>
            <Button size="sm" variant="light" className="w-100 border" onClick={changeSortMode}>
              <FontAwesomeIcon icon={sortDesc ? solid('sort-numeric-down-alt') : solid('sort-numeric-down')} />
              <span className="ms-2">Sort: <b>{sortDesc ? "descending" : "ascending"}</b></span>
            </Button>
          </Col>
        </Row> */}
      </>
    )
  }

  return (
    <Container fluid="md" className="mt-3">
      {renderToolbar()}

      <hr />

      {renderAsyncContent()}

      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {assetLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {assetLabels.singular} <b>{datasource && datasource.name}</b>?</p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={handleDeleteClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteDatasource}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default memo(DatasourcesList);
